<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref, useId } from "vue";
import { formatDateTime } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import InfoCallout from "@/components/common/InfoCallout.vue";
import CnmcAllocationSummary from "./CnmcAllocationSummary.vue";
import CnmcDialog from "./CnmcDialog.vue";
import CnmcApplicationCard from "./CnmcApplicationCard.vue";
import CnmcReasonForm from "./CnmcReasonForm.vue";
import CnmcNotice from "./CnmcNotice.vue";
import CnmcPlanProgress from "./CnmcPlanProgress.vue";
import { isIssuance, issuanceFileCount, planPeriod } from "./cnmcPresentation";
import { cnmcApi } from "./cnmcApi";
import type { CnmcPlan } from "./cnmcTypes";
import { useCnmcApplicationActions } from "./useCnmcApplicationActions";

const props = withDefaults(
  defineProps<{
    companyId: number;
    plan: CnmcPlan;
    canPrepare: boolean;
    canReview?: boolean;
    isAdmin?: boolean;
    canEdit?: boolean;
    showFilingDetails?: boolean;
    showNavigation?: boolean;
  }>(),
  { canEdit: true, showFilingDetails: false, showNavigation: true }
);
const emit = defineEmits<{
  update: [plan: CnmcPlan];
  close: [];
  edit: [plan: CnmcPlan];
  filingDetails: [];
}>();
const busy = defineModel<boolean>("busy", { default: false });
const showDeleteConfirmation = ref(false);
const { globalErrorHandler } = useGlobalHandlers();
const { locale } = useI18n();
const id = useId();
const ppas = computed(() => [
  ...new Map(
    props.plan.allocations.flatMap((allocation) =>
      allocation.ppa_portfolio_id
        ? [
            [
              allocation.ppa_portfolio_id,
              allocation.ppa_portfolio_name,
            ] as const,
          ]
        : []
    )
  ),
]);
const hasMarket = computed(() =>
  props.plan.allocations.some((allocation) => allocation.purpose === "MARKET")
);
const readyAllocations = computed(() =>
  props.plan.allocations.filter((allocation) => allocation.ready_for_delivery)
);
const readyPpas = computed(
  () =>
    new Set(
      readyAllocations.value.map((allocation) => allocation.ppa_portfolio_id)
    )
);
const marketIssuanceRecorded = computed(() =>
  readyAllocations.value.some((allocation) => allocation.purpose === "MARKET")
);
const stages = computed(() => [
  {
    key: "issuance_stage",
    applications: props.plan.applications.filter(isIssuance),
  },
  {
    key: "delivery_stage",
    applications: props.plan.applications.filter(
      (application) => !isIssuance(application)
    ),
  },
]);
const devices = computed(() => [
  ...new Map(
    props.plan.inputs.production_sources.map((source) => [
      source.registry_device_id,
      source,
    ])
  ).values(),
]);
const refresh = async () =>
  emit(
    "update",
    (
      await cnmcApi.plan(
        props.companyId,
        props.plan.registry_connection_id,
        props.plan.id
      )
    ).data
  );
const { perform, applicationAction } = useCnmcApplicationActions({
  companyId: () => props.companyId,
  connectionId: () => props.plan.registry_connection_id,
  busy,
  update: (view) => {
    if (view.kind === "PLAN") emit("update", view.plan);
  },
  refresh,
});
const deleteDraft = async () => {
  if (busy.value || !props.canEdit || props.plan.status !== "DRAFT") return;
  busy.value = true;
  try {
    await cnmcApi.deleteDraft(
      props.companyId,
      props.plan.registry_connection_id,
      props.plan.id
    );
    showDeleteConfirmation.value = false;
    emit("close");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
  }
};
const confirm = () =>
  perform(async () => {
    const { data } = await cnmcApi.confirm(
      props.companyId,
      props.plan.registry_connection_id,
      props.plan.id
    );
    emit("update", data);
  });
const deliver = (ppaId: number | null) =>
  perform(async () => {
    const { data } = await cnmcApi.delivery(
      props.companyId,
      props.plan.registry_connection_id,
      props.plan.id,
      ppaId
    );
    emit("update", data);
  });
const checkAll = () =>
  perform(async () => {
    for (const application of props.plan.applications.filter(
      (item) =>
        item.receipt && !["ABANDONED", "SUPERSEDED"].includes(item.status)
    )) {
      await cnmcApi.check(
        props.companyId,
        props.plan.registry_connection_id,
        application.id
      );
    }
    await refresh();
  });
const abandon = (reason: string) =>
  perform(async () => {
    await cnmcApi.abandon(
      props.companyId,
      props.plan.registry_connection_id,
      props.plan.id,
      reason
    );
    await refresh();
  });
</script>

<template>
  <section class="py-0">
    <nav
      v-if="showNavigation"
      class="d-flex align-items-center gap-2 small mb-3"
      :aria-label="$t('cnmc.batch')"
    >
      <button class="btn btn-link btn-sm p-0" @click="emit('close')">
        {{ $t("tradingCenter.tabs.issuance_reporting.title") }}
      </button>
      <span aria-hidden="true">›</span
      ><span class="text-body-secondary">{{
        $t("cnmc.batch_number", { id: plan.id })
      }}</span>
    </nav>
    <header
      class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4"
    >
      <div>
        <h4 class="font-serif mb-1">
          {{
            planPeriod(plan, locale) || $t("cnmc.batch_number", { id: plan.id })
          }}
        </h4>
        <p class="text-body-secondary small mb-1">
          {{ $t("cnmc.batch_number", { id: plan.id }) }} ·
          {{ formatDateTime(plan.confirmed_at || plan.created_at, locale) }}
        </p>
        <div
          v-for="device in devices"
          :key="device.registry_device_id"
          class="small text-body-secondary text-break"
        >
          {{ device.device_name }} · CIL {{ device.cil }}
        </div>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <button
          v-if="showFilingDetails"
          class="btn btn-outline-secondary btn-sm"
          :disabled="busy || !canPrepare"
          @click="emit('filingDetails')"
        >
          {{ $t("cnmc.filing_details") }}
        </button>
        <button
          v-if="plan.applications.some((application) => application.receipt)"
          class="btn btn-outline-primary btn-sm"
          :disabled="busy"
          @click="checkAll"
        >
          {{ $t("cnmc.check_status") }}
        </button>
      </div>
    </header>

    <CnmcAllocationSummary :plan class="mb-4" />
    <div class="d-flex align-items-center flex-wrap gap-3 mb-4">
      <CnmcPlanProgress :plan />
      <span class="small fw-medium">{{
        $t(`cnmc.actions.${plan.next_action}`, issuanceFileCount(plan))
      }}</span>
    </div>
    <CnmcNotice v-if="plan.progress === 'NEEDS_ATTENTION'" class="mb-3">{{
      $t("cnmc.needs_attention_explanation")
    }}</CnmcNotice>
    <div v-if="plan.status === 'DRAFT'" class="d-flex flex-wrap gap-2 mb-4">
      <button
        v-if="canEdit"
        class="btn btn-outline-secondary"
        :disabled="busy || !canPrepare"
        @click="emit('edit', plan)"
      >
        {{ $t("cnmc.edit_draft") }}
      </button>
      <button
        class="btn btn-primary"
        :disabled="busy || !canPrepare"
        @click="confirm"
      >
        {{ $t("cnmc.confirm_and_generate") }}
      </button>
      <button
        v-if="canEdit"
        class="btn btn-outline-danger ms-sm-auto"
        :disabled="busy"
        @click="showDeleteConfirmation = true"
      >
        {{ $t("cnmc.delete_draft") }}
      </button>
    </div>

    <CnmcDialog
      v-model="showDeleteConfirmation"
      :title="$t('cnmc.delete_draft')"
      :busy
    >
      <p class="p-4 mb-0">
        {{ $t("cnmc.delete_draft_confirmation", { id: plan.id }) }}
      </p>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 border-top p-3">
          <button
            class="btn btn-outline-secondary"
            :disabled="busy"
            @click="showDeleteConfirmation = false"
          >
            {{ $t("common.cancel") }}
          </button>
          <button class="btn btn-danger" :disabled="busy" @click="deleteDraft">
            {{ $t("cnmc.delete_draft") }}
          </button>
        </div>
      </template>
    </CnmcDialog>

    <details v-if="plan.allocations.length" class="mb-4">
      <summary class="small fw-semibold">{{ $t("cnmc.allocation") }}</summary>
      <div class="table-responsive mt-2">
        <table class="table table-padded table-sm align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>{{ $t("cnmc.month") }}</th>
              <th>{{ $t("cnmc.recipient_name") }}</th>
              <th>{{ $t("cnmc.route") }}</th>
              <th>GOs</th>
              <th>{{ $t("cnmc.delivery_due") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="allocation in plan.allocations" :key="allocation.id">
              <td>{{ allocation.production_month.slice(0, 7) }}</td>
              <td>
                {{ allocation.recipient_snapshot.name
                }}<span
                  v-if="allocation.ppa_portfolio_id"
                  class="text-body-secondary"
                >
                  · {{ allocation.ppa_portfolio_name }}</span
                >
              </td>
              <td>{{ $t(`cnmc.routes.${allocation.route}`) }}</td>
              <td>{{ $n(allocation.go_count) }}</td>
              <td>
                {{
                  allocation.ready_for_delivery
                    ? $t("cnmc.ready_for_filing")
                    : "—"
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>

    <InfoCallout
      v-if="plan.status === 'CONFIRMED'"
      icon="file-lines"
      class="mb-4"
    >
      {{ $t("cnmc.stacking_instructions") }}
      <a href="https://gdo.cnmc.es/" target="_blank" rel="noopener noreferrer"
        >CNMC ↗</a
      >
    </InfoCallout>
    <section
      v-for="(stage, index) in stages"
      v-show="plan.status !== 'DRAFT' || stage.applications.length"
      :key="stage.key"
      class="py-0 mb-4"
      :aria-labelledby="`${id}-${stage.key}`"
    >
      <div class="d-flex align-items-center gap-3 mb-3">
        <h5 :id="`${id}-${stage.key}`" class="eyebrow-label mb-0">
          {{ $t(`cnmc.${stage.key}`) }}
        </h5>
        <span class="border-top flex-grow-1" />
      </div>
      <div
        v-if="index === 1 && plan.status === 'CONFIRMED'"
        class="d-flex flex-wrap align-items-start gap-2 mb-3"
      >
        <div v-for="[ppaId, ppaName] in ppas" :key="ppaId">
          <button
            class="btn btn-outline-primary btn-sm"
            :disabled="
              busy ||
              !canPrepare ||
              plan.progress === 'NEEDS_ATTENTION' ||
              !readyPpas.has(ppaId)
            "
            :aria-describedby="
              !readyPpas.has(ppaId)
                ? `${id}-ppa-${ppaId}-issuance-required`
                : undefined
            "
            @click="deliver(ppaId)"
          >
            {{ $t("cnmc.prepare_ppa_delivery", { name: ppaName }) }}
          </button>
          <p
            v-if="!readyPpas.has(ppaId)"
            :id="`${id}-ppa-${ppaId}-issuance-required`"
            class="small text-body-secondary mt-2 mb-0"
          >
            {{ $t("cnmc.ppa_delivery_issuance_required") }}
          </p>
        </div>
        <div v-if="hasMarket">
          <button
            class="btn btn-outline-primary btn-sm"
            :disabled="
              busy ||
              !canPrepare ||
              plan.progress === 'NEEDS_ATTENTION' ||
              !marketIssuanceRecorded
            "
            :aria-describedby="
              !marketIssuanceRecorded
                ? `${id}-market-issuance-required`
                : undefined
            "
            @click="deliver(null)"
          >
            {{ $t("cnmc.prepare_market_export") }}
          </button>
          <p
            v-if="!marketIssuanceRecorded"
            :id="`${id}-market-issuance-required`"
            class="small text-body-secondary mt-2 mb-0"
          >
            {{ $t("cnmc.market_export_issuance_required") }}
          </p>
        </div>
      </div>
      <div class="cnmc-application-grid">
        <div v-for="application in stage.applications" :key="application.id">
          <CnmcApplicationCard
            :application
            :busy
            :canPrepare
            :canReview
            :isAdmin
            @action="applicationAction(application, $event)"
          />
        </div>
      </div>
      <InfoCallout
        v-if="
          index === 1 &&
          !stage.applications.length &&
          plan.status === 'CONFIRMED'
        "
        icon="clock"
        >{{ $t("cnmc.awaiting_delivery_form") }}</InfoCallout
      >
    </section>

    <div
      v-if="isAdmin && plan.status !== 'ABANDONED'"
      class="alert alert-subtle-warning admin-extra-info mt-4 mb-0 small"
    >
      <details
        v-if="canPrepare && canEdit && plan.status === 'CONFIRMED'"
        class="mb-3"
      >
        <summary>{{ $t("cnmc.replacement_draft") }}</summary>
        <p class="my-2 text-body-secondary">
          {{ $t("cnmc.replacement_draft_help") }}
        </p>
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="busy"
          @click="emit('edit', plan)"
        >
          {{ $t("cnmc.replacement_draft") }}
        </button>
      </details>
      <details>
        <summary>{{ $t("cnmc.abandon_batch") }}</summary>
        <p class="my-2 text-body-secondary">
          {{ $t("cnmc.abandon_explanation") }}
        </p>
        <CnmcReasonForm
          :label="$t('cnmc.abandonment_reason')"
          :submit-label="$t('cnmc.abandon_batch')"
          :disabled="busy"
          button-class="btn-outline-danger"
          @submit="abandon"
        />
      </details>
    </div>
  </section>
</template>

<style scoped lang="scss">
.cnmc-application-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
  gap: 12px;
}
</style>
